'use client';

import { createContext, ReactNode, useContext, useEffect, useReducer, useRef } from 'react';
import {
  IMarketArticlesFilterPayload,
} from '@/creditflowresearch/domain/vo/ArticlesAndStats/Articles/IMarketArticle';
import reducer, { initialState } from './reducer';
import useActions from './useActions';
import useParentIssuerSelect from './useParentIssuerSelect';
import useDealSelect from './useDealSelect';
import { initialArticlesFilterState } from '@/creditflowresearch/application/ArticlesAndStats/Articles/constants';
import { useRouter } from 'next/router';
import Market from '@/creditflowresearch/domain/Market';
import Segment from '@/creditflowresearch/domain/Segment';
import Product from '@/creditflowresearch/domain/Product';
import Region from '@/creditflowresearch/domain/Region';
import { adaptFilters } from '@/creditflowresearch/application/ArticlesAndStats/Articles/serverSide/filterAdapter';

interface IUseArticlesFilterProps {
  filtersState: IMarketArticlesFilterPayload,
  filtersPayload: IMarketArticlesFilterPayload,
  hasFilters: boolean,
}

const useArticlesFilter = ({
  filtersState,
  filtersPayload,
  hasFilters,
}: IUseArticlesFilterProps) => {
  const [state, dispatch] = useReducer(reducer, { ...initialState, filtersPayload });
  const { query } = useRouter();
  const { hash, regions, products, markets, segments } = query;
  const actions = useActions(dispatch);
  const parentIssuer = useParentIssuerSelect();
  const { regionList } = state.items.filter;
  const deals = useDealSelect({ regionList });
  const isInitialized = useRef(false);

  useEffect(() => {
    parentIssuer.initParentIssuer(filtersPayload.includedParentIds, filtersPayload.includedIssuerIds);
    deals.initDeals(filtersPayload.includedDealIds);

    if (hasFilters) {
      actions.initFilters(filtersPayload, filtersState);
    }
  }, []);

  useEffect(() => {
    const hasUrlParams = !hash && (regions || markets || segments || products);

    const buildFiltersFromUrl = () => adaptFilters({
      ...initialArticlesFilterState,
      includedProductIds: Product.getIds(products),
      includedRegionIds: Region.getIds(regions),
      includedMarketIds: Market.getIds(markets),
      includedSegmentIds: Segment.getIds(segments),
    }, { markets, segments });

    const filtersToUse = hasUrlParams ? buildFiltersFromUrl() : filtersPayload;

    if (!isInitialized.current) {
      isInitialized.current = true;
      actions.payloadChange(filtersToUse);
      return;
    }

    if (hasUrlParams) {
      if (hasFilters) {
        actions.initFilters(filtersToUse, filtersState);
      }
      actions.payloadChange(filtersToUse);
    }
  }, [products, regions, markets, segments]);

  return {
    state,
    deals,
    parentIssuer,
    ...actions,
  };
};

type ArticlesFilterContextType = ReturnType<typeof useArticlesFilter>;

export const ArticlesFilterContext = createContext<ArticlesFilterContextType>(null);

export const useArticlesFilterContext = () => {
  const context = useContext(ArticlesFilterContext);

  if (!context) {
    throw new Error('useArticlesFilterContext must be used within the ArticlesFilterContext');
  }

  return context;
};

interface IProps {
  children: ReactNode,
  filtersState: IMarketArticlesFilterPayload,
  filtersPayload: IMarketArticlesFilterPayload,
  hasFilters: boolean,
}

const ArticlesFilterContextProvider = ({ children, ...otherProps }: IProps) => (
  <ArticlesFilterContext.Provider value={ useArticlesFilter(otherProps) }>
    { children }
  </ArticlesFilterContext.Provider>
);

export default ArticlesFilterContextProvider;
